import { useState, type ReactNode } from "react";
import Helmet from "react-helmet";
import { IoCodeSlashOutline, IoCreateOutline } from "react-icons/io5";
import { useNavigate, useOutletContext } from "react-router";
import styled from "styled-components";

import CodeEditor from "./CodeEditor";
import { StyledEditorLayout } from "./StyledEditorLayout";
import useNotifications, { notifyMutationErrors } from "../../../components/Notifications";
import * as UI from "../../../modules/ui-kit";
import {
    useUnsavedChangesEditor,
    type UnsavedChangesContext,
} from "../modules/unsaved-changes";

const StyledEntityCreateForm = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 100%;
    min-height: 0;

    .title-row {
        border-bottom: 1px solid #ebecf0;
        padding-bottom: 16px;

        h1 {
            color: #172b4d;
            font-size: 1.35rem;
            font-weight: 700;
            letter-spacing: -0.01em;
            margin: 0;
        }
    }
`;

export type CreateResult = {
    errors?: { message: string, path?: string[] }[];
    redirectTo?: string;
};

interface IEntityCreateFormProps {
    /** Label of the primary button, e.g. "Create BimClass". */
    createLabel: string;
    /** Form-mode starting values; also the baseline the dirty check compares against. */
    defaultValues: Record<string, unknown>;
    /** Text under the JSON Source tab's label, listing the fields the definition accepts. */
    jsonDescription: string;
    /** Runs the mutation. Return the server's `errors`, or where to go on success. */
    onCreate: (definition: unknown) => Promise<CreateResult>;
    renderFields: (context: {
        saving: boolean,
        setValue: (key: string, value: unknown) => void,
        values: Record<string, unknown>,
    }) => ReactNode;
    title: string;
    /** Maps form values to the mutation's definition payload. */
    toDefinition: (values: Record<string, unknown>) => unknown;
}

export default function EntityCreateForm(props: IEntityCreateFormProps) {
    const {
        createLabel,
        defaultValues,
        jsonDescription,
        onCreate,
        renderFields,
        title,
        toDefinition,
    } = props;

    const guard = useOutletContext<UnsavedChangesContext>();
    const navigate = useNavigate();
    const notifications = useNotifications();

    const [mode, setMode] = useState<"form" | "json">("form");
    const [values, setValues] = useState(defaultValues);
    const [jsonText, setJsonText] = useState(() => JSON.stringify(toDefinition(defaultValues), null, 2));
    const [jsonError, setJsonError] = useState(undefined);
    const [jsonParsed, setJsonParsed] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const defaultDefinitionText = JSON.stringify(toDefinition(defaultValues));

    const dirty = mode === "form"
        ? JSON.stringify(values) !== JSON.stringify(defaultValues)
        : (jsonParsed !== undefined && JSON.stringify(jsonParsed) !== defaultDefinitionText);

    function setValue(key: string, value: unknown) {
        setValues((current) => ({ ...current, [key]: value }));
    }

    function onJsonChange(value: string | undefined) {
        const text = value ?? "";
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            setJsonParsed(parsed);
            setJsonError(undefined);
        } catch (e) {
            setJsonError((e as Error).message);
        }
    }

    function switchMode(targetMode: "form" | "json") {
        setJsonError(undefined);
        if (targetMode === "json") {
            setJsonText(JSON.stringify(toDefinition(values), null, 2));
        }
        setMode(targetMode);
    }

    async function save() {
        if (mode === "json" && jsonError) return false;

        setSaving(true);

        try {
            const definition = mode === "json" && jsonParsed
                ? jsonParsed
                : toDefinition(values);

            const result = await onCreate(definition);

            if (result?.errors?.length) {
                notifyMutationErrors(notifications, result.errors);
                return false;
            }

            setValues(defaultValues);
            setJsonParsed(undefined);

            notifications.success("Created.");

            if (result?.redirectTo) navigate(result.redirectTo);

            return true;
        } finally {
            setSaving(false);
        }
    }

    useUnsavedChangesEditor(guard, save, dirty);

    function onSubmit(event) {
        event.preventDefault();
        void save();
    }

    function onReset() {
        setValues(defaultValues);
        setJsonText(JSON.stringify(toDefinition(defaultValues), null, 2));
        setJsonError(undefined);
        setJsonParsed(undefined);
    }

    return (
        <StyledEntityCreateForm>
            <Helmet>
                <title>{ title } - taxonomy</title>
            </Helmet>
            <div className="title-row">
                <h1>{ title }</h1>
            </div>

            <StyledEditorLayout>
                <div className="mode-tabs">
                    <button
                        className={ mode === "form" ? "active" : "" }
                        onClick={ () => switchMode("form") }
                        type="button"
                    >
                        <IoCreateOutline /> Form View
                    </button>
                    <button
                        className={ mode === "json" ? "active" : "" }
                        onClick={ () => switchMode("json") }
                        type="button"
                    >
                        <IoCodeSlashOutline /> JSON Source
                    </button>
                </div>

                <UI.Form
                    className="editor-body"
                    onReset={ onReset }
                    onSubmit={ onSubmit }
                >
                    { mode === "form" ? renderFields({ saving, setValue, values }) : (
                        <UI.Field className="json-field">
                            <UI.FieldLabel>Raw Definition</UI.FieldLabel>
                            <UI.FieldDescription>{ jsonDescription }</UI.FieldDescription>
                            <div className="json-editor-wrapper">
                                <CodeEditor
                                    key={ mode }
                                    onChange={ onJsonChange }
                                    value={ jsonText }
                                />
                            </div>
                            { jsonError && <span className="errors">Invalid JSON: { jsonError }</span> }
                        </UI.Field>
                    ) }

                    <UI.ButtonGroup>
                        <UI.Button
                            disabled={ !dirty || saving }
                            type="reset"
                        >
                            Reset
                        </UI.Button>
                        <UI.Button
                            $appearance="primary"
                            disabled={ !dirty || (mode === "json" && !!jsonError) || saving }
                            type="submit"
                        >
                            { saving ? "Creating…" : createLabel }
                        </UI.Button>
                    </UI.ButtonGroup>
                </UI.Form>
            </StyledEditorLayout>
        </StyledEntityCreateForm>
    );
}
